import React from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://gmreae.com';
const LOGO_URL = '/brand/guleed-logo-dark.png';

// Safely serialize JSON-LD: escape HTML-significant chars to prevent injection
// even though our schema values are admin-controlled.
// NOTE: DOMPurify is for HTML sanitization, not JSON. For `<script type="application/ld+json">`
// the correct hardening is escaping `<`, `>`, `&` which would otherwise close the script tag.
const safeJsonLd = (obj) =>
  JSON.stringify(obj)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');

function OrganizationSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    "name": "Guleed Mortgage",
    "alternateName": "جُليد للرهن العقاري",
    "url": SITE_URL,
    "logo": LOGO_URL,
    "description": "Guleed Mortgage is a fee-free mortgage brokerage in Abu Dhabi helping UAE residents, expats, and non-residents secure the best mortgage rates from leading UAE banking partners.",
    "foundingDate": "2023",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "3 Sails Tower, Corniche",
      "addressLocality": "Abu Dhabi",
      "addressCountry": "AE"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+971-52-289-8249",
      "contactType": "customer service",
      "availableLanguage": ["English", "Arabic"],
      "areaServed": "AE"
    },
    "sameAs": [],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "500",
      "bestRating": "5"
    },
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "value": "15"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Arab Emirates"
    },
    "knowsAbout": [
      "Mortgage Brokerage",
      "Crypto to Property Conversion",
      "Real Estate Investment Advisory",
      "Non-Resident Mortgages UAE"
    ]
  };

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }} />
  );
}

function BreadcrumbSchema({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.path}`
    }))
  };

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }} />
  );
}

function FAQSchema({ faqs }) {
  if (!faqs || faqs.length === 0) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }} />
  );
}

function ArticleSchema({ article, lang }) {
  if (!article) return null;
  const isAr = lang === 'ar';
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": isAr ? article.title_ar : article.title_en,
    "description": isAr ? article.excerpt_ar : article.excerpt_en,
    "image": article.image,
    "author": {
      "@type": "Organization",
      "name": article.author || "Guleed Mortgage Research",
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guleed Mortgage",
      "logo": { "@type": "ImageObject", "url": LOGO_URL }
    },
    "datePublished": article.published_at,
    "dateModified": article.published_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}/blog/${article.slug}`
    },
    "keywords": article.seo_keywords?.join(', ')
  };

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }} />
  );
}

function ServiceSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Mortgage Brokerage",
    "provider": {
      "@type": "FinancialService",
      "name": "Guleed Mortgage"
    },
    "areaServed": { "@type": "Country", "name": "United Arab Emirates" },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Mortgage Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "First Home Buyer Mortgage", "description": "Mortgage from 3.75% fixed rate for UAE first-time buyers" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Non-Resident Mortgage", "description": "Mortgage solutions for overseas investors in UAE property" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Crypto to Property", "description": "Convert digital assets to AED for UAE property purchase" }}
      ]
    }
  };

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }} />
  );
}

export { OrganizationSchema, BreadcrumbSchema, FAQSchema, ArticleSchema, ServiceSchema };
